import { useState, useEffect, Fragment } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import PlayerSearchInput from '../components/PlayerSearchInput'
import CourseSearch, { courseSubtitle } from '../components/CourseSearch'
import ParPickerModal from '../components/ParPickerModal'
import PreRoundGamesPanel, {
  toCreateGamePayload,
  stagedGameAckOk,
} from '../components/PreRoundGamesPanel'
import { useConfirm } from '../components/ConfirmModal'
import { db } from '../lib/db'
import { classifyRoundLiveness } from '../lib/activeRound'
import { searchCourse } from '../lib/searchCourse'
import { teeKey, teeGenderSuffix, resolveSeatTeeKey } from '../lib/tee'
import { invalidateStagedGames } from '../lib/sideGameValidation'
import {
  deriveRoundSetupFromPrefill,
  resizeOtherPlayers,
  readStashedPrefill,
  clearStashedPrefill,
} from '../lib/scheduledRound'

// Team-setup grid cell styles — extracted so the JSX stays readable.
const teamHeader = {
  fontSize: '0.65rem', textAlign: 'center', color: 'var(--gray-300)',
  textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600,
  padding: '4px 2px',
}
const teamRowName = {
  fontWeight: 600, fontSize: '0.9rem',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  paddingRight: 6,
}
const teamCheckCell = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '8px 0', cursor: 'pointer',
}

const ROUND_TYPES = [
  { id: 'stroke', label: 'Stroke Play', desc: 'Individual scoring — every stroke counts. Counts toward your TournyFlex Index when attested.', icon: '🏌️' },
  { id: 'scramble', label: 'Scramble', desc: 'Team picks the best shot and everyone plays from there. Pick up and play. Analysis-only — does not affect your index.', icon: '👥' },
  { id: 'match', label: 'Match Play', desc: 'Head-to-head, hole by hole. Win more holes than remain and the match is closed out. Analysis-only.', icon: '⚔️' },
  { id: '2v2', label: 'Best Ball', desc: 'Two teams of two. Each player plays their own ball; the team takes the lower score per hole. Analysis-only.', icon: '🤝' },
]

export default function PlayPage() {
  const { player } = useAuth()
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const location = useLocation()
  // Prefill transport (F-079): prefer the live router state, but fall back to
  // the sessionStorage stash HomePage wrote before navigating. The stash is the
  // one that actually survives the F-074 service-worker-update RELOAD (and a
  // Capacitor WebView remount) that fires on the very Home→/play navigation
  // carrying a scheduled round — in-memory location.state does not, which is
  // why nothing pre-populated in the field. Read once at mount so a later
  // location.state change (e.g. a same-route BottomNav "Play" tap clearing it)
  // can't wipe an already-initialized setup.
  const [prefill] = useState(() => location.state?.prefill ?? readStashedPrefill())
  const initialSetup = useState(() => deriveRoundSetupFromPrefill(prefill))[0]
  // Default to stroke — only round type available when playerCount is 1 (the
  // default), so it's pre-selected and ready to start. When the user bumps
  // playerCount up they can pick a different format; bumping back to 1
  // collapses the selection back to stroke (see updatePlayerCount).
  const [selectedType, setSelectedType] = useState('stroke')
  const [infoFor, setInfoFor] = useState(null) // id of round-type tile whose info popover is open

  // Team Setup popup (Phase 1 of the round-types build) — opens before the
  // round starts for any non-stroke format. Captures per-player team and
  // per-player "use handicap for scoring" preferences. The popup defers to
  // the actual round start (see commitRound below) once the user confirms.
  const [teamSetupOpen, setTeamSetupOpen] = useState(false)
  const [teamAssign, setTeamAssign] = useState({}) // {playerIdx: teamIdx}
  const [useHcp, setUseHcp] = useState({})         // {playerIdx: bool}
  const [numTeams, setNumTeams] = useState(2)      // 2 for match/2v2 (fixed); 1+ for scramble
  const [holes, setHoles] = useState(initialSetup.holes)
  // Which 9 to play on an 18-hole course when holes='9'. 'front' = holes 1-9,
  // 'back' = holes 10-18. Hidden for 18-hole rounds or 9-hole-only courses.
  const [nineSet, setNineSet] = useState(initialSetup.nineSet)
  const [playerCount, setPlayerCount] = useState(initialSetup.playerCount)
  // Seeded from the scheduled round's roster (F-079) so pre-picked friends —
  // name, phone, linked player, handicap — arrive filled in, not as blank slots.
  const [otherPlayers, setOtherPlayers] = useState(initialSetup.otherPlayers)
  // Expand the "Other Players" section up-front when the schedule carried real
  // players, so the user SEES them without tapping "Add friends now?".
  const [addFriends, setAddFriends] = useState(initialSetup.addFriends)
  const [selectedTee, setSelectedTee] = useState(null)
  // Per-player tee overrides (T3), keyed by seat index (0 = current user,
  // 1..N-1 = otherPlayers). A seat absent from this map inherits selectedTee
  // (the round-level default). Threaded into activeRound.players[].tee so each
  // player's round_scores row is written with THAT player's tee.
  const [playerTees, setPlayerTees] = useState({})
  // Local-only UI: which added-player seats have their "Alternate Tee
  // Selection" dropdown expanded. Seat-index-keyed (matches playerTees).
  // Purely presentational — NOT persisted into the round payload. A seat's
  // absence here just means the alternate picker is collapsed; the seat still
  // inherits the Primary Tees until a playerTees[seat] value is chosen.
  const [expandedTeeSeats, setExpandedTeeSeats] = useState(() => new Set())

  // Pre-round staged side games. Each entry: { tempId, format, config }.
  // Actual create_game calls fire from commitRound after
  // init_standalone_round succeeds — see the finalizedPlayerIds walk below.
  // Kept as an unadorned array in local state; not persisted anywhere
  // between visits (staging pre-round is intentionally ephemeral).
  const [stagedGames, setStagedGames] = useState([])
  // Transient banner text explaining WHY a staged game was auto-unstaged
  // when the round's player count changed (R3 of sidegames-ux). Rendered
  // as a persistent inline notice above the Start Round button; dismissed
  // when the user clears it or bumps player count again to a valid shape.
  const [stagingNotice, setStagingNotice] = useState(null)

  const [courseName, setCourseName] = useState('')
  const [selectedCourse, setSelectedCourse] = useState(null)
  // Manual-mode par picker — only relevant when the selected course is a
  // 'manual' stub (no provider data). The pars array mirrors the modal's
  // last-saved state; null means the user hasn't customized yet and the
  // course is still on the default all-par-4 from create_manual_course.
  const [parPickerOpen, setParPickerOpen] = useState(false)
  const [manualPars, setManualPars] = useState(null)
  const [parsSaving, setParsSaving] = useState(false)
  const isManualCourse = selectedCourse?.source === 'manual'
  // Reset the par state whenever the user picks a different course or
  // changes hole count — stale pars from a prior 18-hole pick shouldn't
  // bleed into a 9-hole config.
  useEffect(() => { setManualPars(null) }, [selectedCourse?.id, holes])
  // Drop per-player tee overrides when the course changes — a tee key from a
  // prior course won't exist on the new one; clearing lets every seat fall
  // back to the fresh default tee.
  useEffect(() => { setPlayerTees({}); setExpandedTeeSeats(new Set()) }, [selectedCourse?.id])
  const [searching, setSearching] = useState(false)
  // F-079: scheduled-round course hydration status. 'loading' while the by-id
  // lookup is in flight, 'error' if it failed or returned no row — surfaced as
  // a visible banner + Retry instead of the old empty catch that left the
  // course silently blank (the user then thought "the course didn't transfer").
  const [courseLoadState, setCourseLoadState] = useState(prefill?.course_id ? 'loading' : 'idle')
  const [courseHydrateNonce, setCourseHydrateNonce] = useState(0)

  // If we arrived from a scheduled round, hydrate the course by id.
  useEffect(() => {
    if (!prefill?.course_id) return
    let cancelled = false
    setCourseLoadState('loading')
    ;(async () => {
      try {
        const data = await searchCourse({ id: prefill.course_id })
        const c = data.courses?.[0]
        if (cancelled) return
        if (!c) throw new Error('course not found')
        setSelectedCourse(c)
        setCourseName(c.name)
        setSelectedTee(defaultTeeForPlayer(c.tees, player?.gender))
        setCourseLoadState('idle')
      } catch (e) {
        if (cancelled) return
        // Visible failure, not a silent swallow — the user can retry the
        // lookup or pick the course manually below.
        // eslint-disable-next-line no-console
        console.error('[scheduled prefill] course hydrate failed:', e?.message || e)
        setCourseLoadState('error')
      }
    })()
    return () => { cancelled = true }
  }, [prefill?.course_id, player?.gender, courseHydrateNonce])

  // Clear the sessionStorage prefill stash when the user genuinely leaves the
  // play-setup surface (React unmount). A full-page RELOAD does NOT run React
  // cleanup, so the stash survives the F-074 SW-update reload and re-seeds the
  // remounted setup; only a deliberate navigation away drops it, so a later
  // manual "Play" open starts clean instead of re-prefilling an abandoned
  // scheduled round.
  useEffect(() => {
    return () => { clearStashedPrefill() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pickCourse = async (c) => {
    // CourseSearch resolves the club to a course row (id + external_course_id).
    // Hydrate by id — that triggers detail-on-select server-side (tees/yardage/
    // GPS) — before using it.
    let course = c
    if ((!c.holes_data || c.holes_data.length === 0 || !c.external_course_id) && c.id) {
      setSearching(true)
      try {
        const data = await searchCourse({ id: c.id })
        if (data.courses?.[0]) course = data.courses[0]
      } catch {}
      setSearching(false)
    }
    setSelectedCourse(course)
    setCourseName(course.name)
    setSelectedTee(defaultTeeForPlayer(course.tees, player?.gender))
    const courseHoleCount = course.holes_data?.length || 18
    if (courseHoleCount <= 9) setHoles('9')
  }

  // Pick the gender-appropriate default tee. Female → first tee with
  // gender='women'; everyone else → first tee with gender='men'; fallback to
  // whatever tees[0] is. Female players can still pick a men's tee via the
  // selector (e.g., playing the Blues for distance practice) — this just
  // sets a sensible default so a female user doesn't see a tee with
  // gender='men' selected by default on round start.
  function defaultTeeForPlayer(tees, gender) {
    const list = tees || []
    if (!list.length) return null
    const want = gender === 'female' ? 'women' : 'men'
    const match = list.find(t => t.gender === want)
    if (match) return teeKey(match)
    return teeKey(list[0])
  }

  const updatePlayerCount = (n) => {
    setPlayerCount(n)
    // Solo round → only stroke is available, collapse to it (and clear any
    // multi-player-only selection like scramble/match/2v2 if it was set).
    if (n === 1) setSelectedType('stroke')
    if (n > 1) {
      // Preserve every already-entered player (name/link/handicap) when the
      // count is bumped up or down — F-079 pre-fills these from the schedule,
      // so a partial-shape rebuild would drop the linked_handicap/handicap
      // fields and blank out pre-picked friends.
      setOtherPlayers(prev => resizeOtherPlayers(prev, n))
    } else {
      setOtherPlayers([])
      setAddFriends(false)
    }
    // R3: staged side games that no longer fit the new player count get
    // auto-unstaged so a Start-blocked-silently loop can't happen. The
    // banner shows plain-language reasons for each removal (e.g. "Vegas
    // removed — needs exactly 4 players (group is 3 now).").
    if (stagedGames.length > 0) {
      const { kept, removed } = invalidateStagedGames(stagedGames, n)
      if (removed.length > 0) {
        setStagedGames(kept)
        setStagingNotice(removed.map(r => r.reason).join(' '))
      } else if (kept.length !== stagedGames.length
                 || kept.some((g, i) => g !== stagedGames[i])) {
        // Seats reconciled in place (e.g. Wolf rotation_order_seats trimmed);
        // clear any lingering notice from an earlier removal since nothing
        // was dropped this time.
        setStagedGames(kept)
        setStagingNotice(null)
      }
    }
  }

  // A staged Vegas game in fixed-teams mode must be a valid 2v2 before the
  // round can start (mirrors StandaloneGamesPanel's "two teams of exactly two"
  // guard). switch6 has no manual split, so it's always valid.
  const stagedVegasInvalid = stagedGames.some(g => {
    if (g.format !== 'vegas') return false
    if (g.config?.teams_mode === 'switch6') return false
    const seats = g.config?.team_seats || []
    const a = seats.filter(t => t === 'A').length
    const b = seats.filter(t => t === 'B').length
    return !(a === 2 && b === 2)
  })
  // F-100-T1 acceptance 2: any staged WAGER game must have the "18 or
  // older" ack ticked before Start Round. Bragging ($0) games and
  // wagerless formats (Shuffle Card) are skipped by stagedGameAckOk.
  const stagedAckMissing = stagedGames.some(g => !stagedGameAckOk(g))
  const canStart = selectedType && selectedCourse && !stagedVegasInvalid && !stagedAckMissing
  // Team formats need team assignments + per-player handicap toggles captured
  // before the round actually starts. Solo stroke skips this entirely.
  // ALSO skip when there are only 2 players in scramble/match — the "team"
  // structure adds no value there (each player is trivially their own
  // team, or shares a team for solo-scramble). Auto-assign at startRound
  // time instead of showing an empty popup.
  const isTwoPlayerNoTeamsNeeded =
    playerCount === 2 && (selectedType === 'scramble' || selectedType === 'match')
  const needsTeamSetup = selectedType !== 'stroke' && !isTwoPlayerNoTeamsNeeded

  const startRound = async () => {
    if (!canStart) return
    if (needsTeamSetup) { openTeamSetup(); return }
    // 2-player scramble/match: commitRound will auto-compute assignments
    // (state updates from here wouldn't be visible synchronously).
    commitRound()
  }

  // Open the team-setup popup with sensible defaults based on round type.
  // Scramble starts as a single team (everyone together) but the user can
  // add more (e.g. 2-team scramble vs. each other). Match Play and Best
  // Ball start as 2 teams with players split evenly by slot.
  const openTeamSetup = () => {
    const split = selectedType === 'scramble' ? 1 : 2
    setNumTeams(split)
    const assigns = {}
    const hcps = {}
    for (let i = 0; i < playerCount; i++) {
      assigns[i] = split === 1 ? 0 : (i < Math.ceil(playerCount / 2) ? 0 : 1)
      // Default: logged-in user uses handicap (we have their tf_index);
      // others default off (we don't reliably have theirs yet — they can
      // flip it on if applicable).
      hcps[i] = i === 0
    }
    setTeamAssign(assigns)
    setUseHcp(hcps)
    setTeamSetupOpen(true)
  }

  const setTeam = (idx, team) => {
    setTeamAssign(prev => ({ ...prev, [idx]: team }))
  }
  const toggleHcp = (idx) => {
    setUseHcp(prev => ({ ...prev, [idx]: !prev[idx] }))
  }
  const addTeam = () => {
    // Scramble only — match/2v2 are fixed at 2 teams. Max teams capped at
    // playerCount - 1 so at least one team always has 2+ players (a roster
    // of all singletons isn't a meaningful "team" round).
    if (selectedType !== 'scramble') return
    if (numTeams >= playerCount - 1) return
    setNumTeams(n => n + 1)
  }
  // For match/2v2: every team must have at least one player. For scramble:
  // any distribution is OK (could even be one team with everyone).
  const teamsValid = () => {
    if (selectedType === 'scramble') return true
    const filled = new Set(Object.values(teamAssign))
    return filled.size >= numTeams
  }

  const confirmTeamSetup = () => {
    if (!teamsValid()) return
    setTeamSetupOpen(false)
    commitRound()
  }

  const commitRound = async () => {
    if (!canStart) return

    // ── Duplicate-round guard ───────────────────────────────────────────
    // Before minting a NEW UUID, check for an existing active_rounds row
    // owned by this player. If one exists, prompt: resume the existing
    // round, or abandon it and start fresh. Without this guard, a client
    // that lost its Home tile (localStorage cleared, foreground-remount
    // race, whatever) proceeds through PlayPage and mints a fresh UUID,
    // splitting the record. Live evidence 2026-07-11 Heather Ridge round
    // where the app fired the round-init sequence twice back-to-back with
    // different UUIDs; scores + shuffle draws landed on two rows and had
    // to be hand-merged (see CLAUDE.md § round_scores.standalone_round_id
    // has no FK). The DB constraint is the tripwire; this is the actual
    // fix.
    if (player?.id) {
      try {
        const { data: existing } = await db('get_active_round', { player_id: player.id })
        // Only the LIVE case blocks a new round. get_active_round returns a
        // soft-deleted round's row regardless of deleted_at (filtering is the
        // client's job — the two-site-sync contract), and DELIBERATELY keeps
        // the active_rounds resume-cache row for the co-player hand-off (D-279).
        // Mirror HomePage: run the row through classifyRoundLiveness and only
        // treat a LIVE round as "already in progress". A DEAD or
        // DELETED_GRABBABLE round falls through to mint a fresh round WITHOUT
        // the popup and WITHOUT deleting the resume cache (that would destroy
        // the co-player hand-off row).
        if (
          existing?.standalone_round_id &&
          existing?.payload &&
          classifyRoundLiveness({ row: existing }) === 'LIVE'
        ) {
          const payload = typeof existing.payload === 'string' ? JSON.parse(existing.payload) : existing.payload
          const courseName = payload?.course?.name || 'a course'
          const holesLabel = payload?.holes === '9'
            ? (payload?.nine_set === 'back' ? '9 (back)' : '9')
            : (payload?.holes || '18')
          const resume = await confirm({
            title: 'Round already in progress',
            message: `You have an active round at ${courseName} (${holesLabel} holes). Resume it or start a new one?\n\nStarting new abandons the existing round's live state — scores already saved on the server stay, but the resume tile goes away.`,
            ok: 'Resume existing',
            cancel: 'Start new anyway',
          })
          if (resume) {
            navigate('/scorecard', { state: { ...payload, standalone: true } })
            return
          }
          // User chose to abandon — delete the old active_rounds row so
          // the resume tile doesn't reappear. round_scores rows stay (the
          // partial round is a real record).
          try {
            await db('delete_active_round', { standalone_round_id: existing.standalone_round_id })
          } catch (e) {
            console.warn('delete_active_round failed:', e?.message || e)
          }
        }
      } catch (e) {
        console.warn('get_active_round pre-check failed; proceeding to new round:', e?.message || e)
      }
    }

    const roundId = crypto.randomUUID()
    // 2-player scramble/match skip the team-setup popup — compute team
    // assignments here so state-lag between startRound and commitRound
    // doesn't matter.
    //   match    → 1v1 (each player their own team)
    //   scramble → same team (solo-scramble collaborators)
    const effectiveTeamAssign = isTwoPlayerNoTeamsNeeded
      ? (selectedType === 'match' ? { 0: 0, 1: 1 } : { 0: 0, 1: 0 })
      : teamAssign
    const effectiveNumTeams = isTwoPlayerNoTeamsNeeded
      ? (selectedType === 'match' ? 2 : 1)
      : numTeams
    // Build per-player team + handicap records, indexed 0 = current user,
    // 1..N-1 = otherPlayers (matching the otherPlayers array offset).
    const players = []
    for (let i = 0; i < playerCount; i++) {
      const isMe = i === 0
      const op = isMe ? null : otherPlayers[i - 1]
      // Handicap resolution priority: user-entered value on this round >
      // linked profile's stored index > the current user's own tf index.
      // NaN / empty string coerces to null so downstream never divides
      // by garbage.
      const myOwnHcp = player?.tf_index_attested ?? player?.tf_index_overall ?? player?.starter_handicap_index ?? null
      const resolvedHcp = isMe
        ? myOwnHcp
        : (op?.handicap != null ? op.handicap : op?.linked_handicap != null ? op.linked_handicap : null)
      const hcpNum = resolvedHcp != null && Number.isFinite(Number(resolvedHcp)) ? Number(resolvedHcp) : null
      players.push({
        slot: i,
        name: isMe
          ? (player?.first_name || player?.name || 'You')
          : (op?.name || `Player ${i + 1}`),
        phone: isMe ? null : (op?.phone || null),
        linked_player_id: isMe ? (player?.id || null) : (op?.linked_player_id || null),
        team: effectiveTeamAssign[i] ?? 0,
        use_handicap: !!useHcp[i],
        handicap_index: hcpNum,
        // Per-player tee (T3): this seat's chosen tee key, defaulting to the
        // round-level tee when the user didn't override it. ScorecardPage's
        // save loop writes each round_scores row with THIS key's tee.
        tee: resolveSeatTeeKey(playerTees[i], selectedTee),
      })
    }
    // F-104.1-T1: hole_count + starting_hole are the SERVER-AUTHORITATIVE
    // length/start inputs; derived here (identically to the init_standalone_round
    // call below) so they ride along in the activeRound payload — ScorecardPage
    // reads starting_hole FIRST (nine_set is now a legacy fallback for in-flight
    // pre-F-104.1 rounds). courseHoleCount defaults to 18 when selectedCourse
    // omits holes_data (manual / gca_undefined) so a back-9 selection on a
    // course with missing/short holes_data still threads starting_hole=10.
    const courseHoleCount = selectedCourse?.holes_data?.length || 18
    const hc = holes === '9' ? 9 : 18
    // F-104.1-T1 review-fix: drop the `courseHoleCount === 18` check so this
    // gate matches the softened nine_set gate below. The two must agree —
    // otherwise a short/degraded holes_data (manual / gca_undefined course row)
    // yields {nine_set:'back', starting_hole:1}, ScorecardPage's cascade reads
    // starting_hole=1, falls back to nine_set='back' → startHole=10, builds
    // holes 10..18 client-side while the server stamps hole_count=9 starting
    // at hole 1 → completion gate can never match. The round-create setup
    // separately guards against back-9 on a 9-hole course (out of scope here).
    const sh = (hc === 9 && nineSet === 'back') ? 10 : 1
    const activeRound = {
      id: roundId,
      course: selectedCourse,
      tee: selectedTee,
      roundType: selectedType,
      holes,
      // F-080: when this round was started from a league week, carry the
      // league_round_id through the round payload → ScorecardPage's save →
      // round_scores.league_round_id, so the round COUNTS toward league
      // standings. Null for ad-hoc / scheduled rounds.
      league_round_id: prefill?.league_round_id || null,
      // F-104.1-T1: server-authoritative length + starting hole. ScorecardPage's
      // holes-array builder reads starting_hole first, falling back to nine_set
      // only for pre-F-104.1 in-flight rounds whose payload lacks it.
      hole_count: hc,
      starting_hole: sh,
      // Legacy convenience field — kept for pre-F-104.1 in-flight payloads +
      // for any read-site that didn't migrate to starting_hole. Threaded here
      // (softened gate, no holes_data length check) so a course row with
      // missing/short holes_data still carries the user's back-9 intent.
      nine_set: holes === '9' ? nineSet : null,
      playerCount,
      // Legacy field kept for back-compat with the existing scorecard read path
      // (stroke flow). Team formats use the richer `players` array below.
      // CRITICAL: include linked_player_id so a friend-search selection
      // re-uses the real player row instead of ScorecardPage creating a new
      // stub. Live evidence 2026-07-01: adding "Kate Drumm" via friend search
      // generated TWO stub Kate rows (4d4c5f62 + 709d4880) because this map
      // dropped the field, and the stub creator at ScorecardPage:264 doesn't
      // see linked_player_id to skip.
      otherPlayers: otherPlayers.map((p, i) => ({
        name: p.name || `Player ${i + 2}`,
        phone: p.phone || null,
        linked_player_id: p.linked_player_id || null,
      })),
      players,
      numTeams: needsTeamSetup ? numTeams : effectiveNumTeams,
      started_at: new Date().toISOString(),
      player_id: player?.id,
      // Stamp for HomePage's multi-device staleness compare (see the
      // activeRound initializer). Higher wins when localStorage and
      // bootstrap cache both have a round.
      updated_at: Date.now(),
    }
    try {
      localStorage.setItem('active_round', JSON.stringify(activeRound))
    } catch {}

    // Pre-seed active_rounds + per-player round_scores rows on round start
    // (no waiting for first hole save). Two reasons:
    //   1. Home page resume tile shows the round on the OTHER player's
    //      device immediately — get_active_round can find it via the
    //      payload.players[].linked_player_id search.
    //   2. scored_by gate has rows to operate on — Player B can claim her
    //      own row from her phone before Player A enters any scores.
    // Non-fatal if it fails — same code path is invoked from the per-hole
    // save in ScorecardPage so the data still arrives if the user scores.
    //
    // Extended 2026-07-14: also eagerly resolve stub player_ids for
    // un-linked otherPlayers whenever games are staged, so every group
    // seat has a real players row referenceable by create_game. Un-named
    // slots ("Player 3") stay stub-less at PlayPage — they get their
    // stub inside ScorecardPage on first save, and the in-round
    // StandaloneGamesPanel can add them to the game later.
    const orderedPlayerIds = [player?.id || null]
    // Eagerly resolve a player_id for EVERY seat, not just staged-game
    // rounds. Rationale (from Jacob's 2026-07-14 test round): rounds
    // with anonymous "Player 3" seats need pre-seeded round_scores rows
    // so (a) the scored_by gate treats the round creator as the default
    // scorer for the whole group and (b) side games staged pre-round
    // can include those seats. Fully-anonymous slots get a stub named
    // `Player N` — the user can rename later from the scorecard.
    if (otherPlayers.length > 0) {
      for (let i = 0; i < otherPlayers.length; i++) {
        const op = otherPlayers[i]
        if (op.linked_player_id) {
          orderedPlayerIds.push(op.linked_player_id)
          continue
        }
        const name = (op.name || '').trim()
        const phone = (op.phone || '').trim()
        try {
          const { data: stub } = await db('find_or_create_player_stub', {
            name: name || `Player ${i + 2}`,
            phone: phone || null,
            standalone_round_id: roundId,
          })
          orderedPlayerIds.push(stub?.id || null)
        } catch (e) {
          console.warn('stub create failed at pre-round stage:', e?.message || e)
          orderedPlayerIds.push(null)
        }
      }
    }
    // Write the resolved player_ids back onto the activeRound payload so
    // ScorecardPage's hydrate path can pick up the stub IDs alongside the
    // linked_player_ids. Otherwise ScorecardPage sees `linked_player_id:
    // null` for a stub seat and creates a DUPLICATE stub inside its own
    // first-save path — the exact bug the 2026-07-01 kate-drumm live
    // evidence comment above warns about.
    if (otherPlayers.length > 0) {
      activeRound.otherPlayers = otherPlayers.map((op, i) => ({
        name: op.name || `Player ${i + 2}`,
        phone: op.phone || null,
        linked_player_id: op.linked_player_id || orderedPlayerIds[i + 1] || null,
      }))
      for (let i = 0; i < activeRound.players.length; i++) {
        if (!activeRound.players[i].linked_player_id) {
          activeRound.players[i].linked_player_id = orderedPlayerIds[i] || null
        }
      }
      try {
        localStorage.setItem('active_round', JSON.stringify(activeRound))
      } catch {}
    }
    if (player?.id && selectedCourse?.id) {
      try {
        // Every resolved player_id gets a pre-seeded round_scores row —
        // stubs included. Two consequences (both intended):
        //   1. scored_by is stamped to the round creator on every seat,
        //      so the scorecard renders the creator as the default scorer
        //      for the whole group until someone else claims from their
        //      own device.
        //   2. Side games staged pre-round can reference any seat
        //      (including anonymous stubs), because the participants
        //      validation passes for every player_id we resolved above.
        const seedIds = orderedPlayerIds.filter(Boolean)
        // F-104: thread the ROUND'S intended length + starting hole onto the
        // standalone_rounds parent row at create time so the server's
        // save-time completion gate knows this round is a declared 9 (vs an
        // 18 that happens to have 9 holes scored so far). hole_count collapses
        // the UI's holes selector ('9'|'18') to a smallint; starting_hole
        // flips to 10 for a back-9 on an 18-hole course. 9-hole-only courses
        // always start at 1 (nineSet is suppressed by the UI). Pre-F-104
        // clients just omit these; the server derives from course.holes.
        // F-104.1-T1: `hc` + `sh` are derived once above (next to the
        // activeRound literal) so both the init payload AND the client-side
        // standaloneState see the same values.
        await db('init_standalone_round', {
          standalone_round_id: roundId,
          owner_player_id: player.id,
          payload: activeRound,
          course_id: selectedCourse.id,
          player_ids: seedIds,
          // F-080: link the pre-seeded round_scores rows to the league week so
          // standings pick the round up the moment a score lands.
          league_round_id: prefill?.league_round_id || null,
          hole_count: hc,
          starting_hole: sh,
        })
      } catch (e) {
        console.warn('init_standalone_round failed:', e.message)
      }
    }

    // Create every staged side game. Errors on any single create are
    // surfaced but non-fatal for the round itself — the user still lands
    // on the scorecard with whatever games did create successfully; they
    // can retry the rest via the in-round StandaloneGamesPanel. The most
    // likely error is server-side Pro-gate rejection when the user stacks
    // 2+ games without an active Pro sub.
    if (stagedGames.length > 0) {
      const gamePlayerIds = orderedPlayerIds.filter(Boolean)
      // Surface the pre-round game creation flow to the console so a
      // failure ("game didn't carry over") is diagnosable via F12. Prior
      // version only stashed the format name in sessionStorage; the
      // actual server rejection text was lost.
      // eslint-disable-next-line no-console
      console.info('[pre-round games]', {
        count: stagedGames.length,
        formats: stagedGames.map(g => g.format),
        player_ids: gamePlayerIds,
      })
      if (gamePlayerIds.length < 2) {
        // Common cause: user staged a game but only 1 seat had a resolved
        // player_id (others were fully-anonymous "Player 3" placeholders).
        // eslint-disable-next-line no-console
        console.warn('[pre-round games] skipped — need ≥2 resolved player_ids, got', gamePlayerIds.length)
        try {
          sessionStorage.setItem('tf:preroundSkipped', JSON.stringify(
            stagedGames.map(g => ({ format: g.format, reason: 'need 2+ named players (fully-anonymous seats need a name)' }))
          ))
        } catch {}
      } else {
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (!session?.access_token) {
            // eslint-disable-next-line no-console
            console.warn('[pre-round games] skipped — no auth session; sign-in stale?')
          }
          const skipped = []
          // F-100-T1 acceptance 4: age-gate rejections are the ONE class of
          // create_game failure that gets a visible, same-page banner instead
          // of the silent sessionStorage['tf:preroundSkipped'] handoff to
          // ScorecardPage. Server codes covered:
          //   - 'under_18_wager_block'   → known-under-18 participant
          //   - 'stub_age_ack_required'  → missing stub_age_ack (edit path
          //                                only today; kept for parity)
          // Any other failure keeps flowing to tf:preroundSkipped so
          // ScorecardPage's delayed toast keeps working (spec 5f).
          const ageErrors = []
          const isAgeGateCode = (c) => c === 'under_18_wager_block' || c === 'stub_age_ack_required'
          for (const staged of stagedGames) {
            // F-100-T1: forward the stub_age_ack ticked in PreRoundGamesPanel.
            // toCreateGamePayload's `{ ...config }` spread carries the field
            // through; server rewrites `true` → `{ by, at }` audit trail (see
            // netlify/functions/standalone-game.js:205).
            const payload = toCreateGamePayload(staged, gamePlayerIds)
            if (!payload) {
              // eslint-disable-next-line no-console
              console.warn('[pre-round games] no payload for', staged.format)
              skipped.push({ format: staged.format, reason: 'client payload build failed' })
              continue
            }
            try {
              const res = await fetch('/.netlify/functions/standalone-game', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${session?.access_token || ''}`,
                },
                body: JSON.stringify({
                  action: 'create_game',
                  standalone_round_id: roundId,
                  format: payload.format,
                  config: payload.config,
                  participants: payload.participants,
                }),
              })
              const bodyJson = await res.json().catch(() => ({}))
              if (!res.ok) {
                // eslint-disable-next-line no-console
                console.warn('[pre-round games] rejected', staged.format, res.status, bodyJson)
                const code = bodyJson?.code || null
                const entry = {
                  format: staged.format,
                  reason: bodyJson?.error || `HTTP ${res.status}`,
                  code,
                }
                if (isAgeGateCode(code)) {
                  ageErrors.push(entry)
                } else {
                  skipped.push(entry)
                }
              } else {
                // eslint-disable-next-line no-console
                console.info('[pre-round games] created', staged.format, bodyJson?.data?.game_id)
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('[pre-round games] fetch failed', staged.format, e)
              skipped.push({ format: staged.format, reason: e?.message || 'network' })
            }
          }
          if (skipped.length > 0) {
            try {
              sessionStorage.setItem('tf:preroundSkipped', JSON.stringify(skipped))
            } catch {}
          }
          // F-100-T1 acceptance 4: any age-gate error blocks navigation and
          // surfaces the block on PlayPage's existing stagingNotice banner.
          // The round row + any successfully-created games are already in
          // the DB — the dup-round guard at the top of commitRound will
          // offer Resume when the user re-taps Start Round after fixing
          // the roster or ticking the ack.
          if (ageErrors.length > 0) {
            const banner = ageErrors.map(e => {
              if (e.code === 'stub_age_ack_required') {
                return `${e.format} didn’t start — confirm every participant is 18+`
              }
              return `${e.format} didn’t start — ${e.reason || 'under-18 participant blocked'}`
            }).join('; ')
            setStagingNotice(banner)
            return
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[pre-round games] outer failure', e)
        }
      }
    }

    // If this round was started from a scheduled round, consume it: delete the
    // scheduled row AND drop the prefill stash so it can't re-seed a later open.
    if (prefill?.scheduled_round_id) {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        await fetch(`/.netlify/functions/scheduled-rounds?id=${prefill.scheduled_round_id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${session?.access_token}` },
        })
      } catch {}
      clearStashedPrefill()
    }

    // One-shot marker so ScorecardPage's resurrection-gate liveness check
    // (ops #42 goal c) SKIPS this brand-new round: init_standalone_round just
    // created the active_rounds row above, and a get_active_round check on
    // mount could race that write's commit and wrongly bounce a legit fresh
    // round to Home. A cache/resume open carries no such marker, so it IS
    // checked. sessionStorage (not location.state) so it never leaks into the
    // persisted round payload and defeat the gate on a later resume.
    try { sessionStorage.setItem('tf_fresh_round', activeRound.id) } catch {}

    navigate('/scorecard', {
      state: {
        ...activeRound,
        standalone: true,
      }
    })
  }

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <p className="eyebrow">New Round</p>
          <h1>Start Playing</h1>
        </div>

        {/* Course search */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Course
          </p>
          {!selectedCourse ? (
            <>
              {/* F-079: scheduled-round course still loading by id. */}
              {courseLoadState === 'loading' && (
                <p className="text-xs text-muted" style={{ marginBottom: 6 }}>Loading your scheduled course…</p>
              )}
              {/* F-079: by-id hydrate failed — visible, retryable, and the
                  manual search below is still available as a fallback. */}
              {courseLoadState === 'error' && (
                <div style={{
                  padding: '10px 12px', marginBottom: 8,
                  background: 'rgba(220,50,50,0.1)', border: '1px solid rgba(220,50,50,0.4)',
                  borderRadius: 'var(--radius)', color: '#ff8080', fontSize: '0.82rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                }}>
                  <span>Couldn’t load your scheduled course. Retry, or pick it below.</span>
                  <button
                    type="button"
                    onClick={() => setCourseHydrateNonce(n => n + 1)}
                    className="btn btn-ghost btn-sm"
                    style={{ flexShrink: 0, padding: '4px 10px' }}
                  >
                    Retry
                  </button>
                </div>
              )}
              <CourseSearch onSelect={pickCourse} />
              {searching && (
                <p className="text-xs text-muted" style={{ marginTop: 6 }}>Loading course…</p>
              )}
            </>
          ) : (
            <div style={{
              padding: '10px 12px',
              background: 'var(--green-dark)', borderRadius: 'var(--radius)',
              border: '1px solid var(--gold)', fontSize: '0.85rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
            }}>
              <span style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <strong>{selectedCourse.club_name || selectedCourse.name}</strong>
                  {isManualCourse && (
                    <span style={{
                      fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                      textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4,
                      background: 'var(--gold)', color: 'var(--green-deep)',
                    }}>
                      Manual Mode
                    </span>
                  )}
                </div>
                {courseSubtitle(selectedCourse) && (
                  <span className="text-muted" style={{ display: 'block', fontSize: '0.78rem', marginTop: 2 }}>
                    {courseSubtitle(selectedCourse)}
                  </span>
                )}
              </span>
              <button
                onClick={() => { setSelectedCourse(null); setCourseName('') }}
                style={{ background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Holes — always shown; 18 button disabled (greyed) when the
            selected course is a 9-hole venue. */}
        {(() => {
          const courseHoleCount = selectedCourse?.holes_data?.length || 18
          const only9 = courseHoleCount <= 9
          return (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Holes
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                {['9', '18'].map(h => {
                  const disabled = only9 && h === '18'
                  return (
                    <button
                      key={h}
                      onClick={() => !disabled && setHoles(h)}
                      disabled={disabled}
                      className={`btn ${holes === h ? 'btn-primary' : 'btn-ghost'}`}
                      style={{ flex: 1, opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    >
                      {h} Holes
                    </button>
                  )
                })}
              </div>
              {/* Front/Back selector — only relevant when playing 9 holes on
                  an 18-hole course (Brighton Dale league: rotate front/back
                  per week per commissioner's plan). On 9-hole-only courses
                  or full 18 rounds it's redundant, so it's hidden. */}
              {courseHoleCount === 18 && holes === '9' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {[
                    { id: 'front', label: 'Front 9' },
                    { id: 'back',  label: 'Back 9' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setNineSet(opt.id)}
                      className={`btn ${nineSet === opt.id ? 'btn-primary' : 'btn-ghost'}`}
                      style={{ flex: 1 }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {/* Manual-course par customization. The stub course starts with
                  par 4 across the board; this lets the user input real pars
                  from the scorecard at the cart shed before teeing off.
                  Made prominent (full-width banner) because the prior
                  dashed-button version was easy to miss — user feedback
                  was "it never asked me to enter par details." */}
              {isManualCourse && (
                <div
                  onClick={() => setParPickerOpen(true)}
                  style={{
                    marginTop: 8,
                    padding: '10px 12px',
                    background: manualPars ? 'var(--green-deep)' : 'rgba(212, 185, 122, 0.15)',
                    border: `1px solid ${manualPars ? 'var(--green-mid)' : 'var(--gold)'}`,
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: manualPars ? 'var(--cream)' : 'var(--gold)' }}>
                      {manualPars
                        ? `Pars set · Total ${manualPars.reduce((s, p) => s + (p || 0), 0)}`
                        : 'Add pars from your scorecard'}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--gray-300)', marginTop: 2 }}>
                      {manualPars
                        ? 'Tap to edit if a hole is off.'
                        : 'Default is par 4 on every hole — +/- par will be wrong until you set real ones.'}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 700,
                    color: 'var(--gold)', whiteSpace: 'nowrap',
                  }}>
                    {manualPars ? 'Edit →' : 'Add →'}
                  </span>
                </div>
              )}
            </div>
          )
        })()}

        {/* Players in Group */}
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Players in Group
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onClick={() => updatePlayerCount(n)}
                className={`btn ${playerCount === n ? 'btn-primary' : 'btn-ghost'}`}
                style={{ flex: 1 }}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Round Type — 1 player: stroke only, full-width;
            2+ players: all four in a 2x2 grid with info popovers. */}
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Round Type
          </p>
          {playerCount === 1 ? (
            // Solo: single full-width Stroke tile (already pre-selected)
            <button
              onClick={() => setSelectedType('stroke')}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: 'var(--gold)', color: 'var(--green-deep)',
                border: '1px solid var(--gold)',
                borderRadius: 'var(--radius-lg)', padding: '12px 18px',
                cursor: 'pointer', textAlign: 'center',
              }}
            >
              <span style={{ fontSize: '1.4rem' }}>🏌️</span>
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Stroke Play</span>
            </button>
          ) : (
            // 2+: 2x2 grid
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {ROUND_TYPES.map(rt => {
                const active = selectedType === rt.id
                return (
                  <div key={rt.id} style={{ position: 'relative' }}>
                    <button
                      onClick={() => setSelectedType(rt.id)}
                      style={{
                        width: '100%',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                        background: active ? 'var(--gold)' : 'var(--green-dark)',
                        color: active ? 'var(--green-deep)' : 'var(--cream)',
                        border: `1px solid ${active ? 'var(--gold)' : 'var(--green-mid)'}`,
                        borderRadius: 'var(--radius-lg)', padding: '14px 8px',
                        cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                        minHeight: 78,
                      }}
                    >
                      <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{rt.icon}</span>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{rt.label}</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setInfoFor(infoFor === rt.id ? null : rt.id) }}
                      aria-label={`About ${rt.label}`}
                      type="button"
                      style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 20, height: 20, borderRadius: '50%',
                        background: 'transparent', border: 'none',
                        color: active ? 'var(--green-deep)' : 'var(--gray-500)',
                        cursor: 'pointer', padding: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
          {/* Info popover: shows the description of whichever tile's "i"
              was tapped. Tap again (or pick another) to dismiss/swap. */}
          {infoFor && (
            <div style={{
              marginTop: 8, padding: '10px 12px',
              background: 'var(--green-dark)', border: '1px solid var(--green-mid)',
              borderRadius: 'var(--radius)', fontSize: '0.82rem', color: 'var(--gray-300)',
              display: 'flex', justifyContent: 'space-between', gap: 8,
            }}>
              <span style={{ flex: 1 }}>
                <strong style={{ color: 'var(--cream)' }}>{ROUND_TYPES.find(rt => rt.id === infoFor)?.label}: </strong>
                {ROUND_TYPES.find(rt => rt.id === infoFor)?.desc}
              </span>
              <button
                onClick={() => setInfoFor(null)}
                aria-label="Close info"
                type="button"
                style={{ background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }}
              >
                ×
              </button>
            </div>
          )}
        </div>

        {selectedType && (
          <>
            {/* Tees — dropdown selection (shown once a round type is picked) */}
            {selectedCourse?.tees?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Primary Tees
                </p>
                <select
                  className="input"
                  value={selectedTee || ''}
                  onChange={e => setSelectedTee(e.target.value)}
                >
                  {selectedCourse.tees.map(t => {
                    // Composite key disambiguates same-color tees with
                    // different gender ratings (Blue 72.1/130 M vs Blue
                    // 77.5/138 W). The "(M)"/"(W)" tag in the label tells
                    // the user which row they're picking.
                    const g = teeGenderSuffix(t)
                    const name = g ? `${t.tee_name} (${g})` : t.tee_name
                    const parts = [name]
                    if (t.total_yardage) parts.push(`${t.total_yardage} yds`)
                    if (t.slope_rating && t.course_rating) parts.push(`${t.course_rating}/${t.slope_rating}`)
                    const key = teeKey(t)
                    return <option key={key} value={key}>{parts.join(' · ')}</option>
                  })}
                </select>
              </div>
            )}

            {/* Add-friends prompt (2+ players, not yet expanded) */}
            {playerCount > 1 && !addFriends && (
              <div style={{ marginBottom: 24 }}>
                <button
                  className="btn btn-ghost btn-full"
                  onClick={() => setAddFriends(true)}
                  style={{ fontSize: '0.9rem', padding: '12px 16px' }}
                >
                  Add friends now?
                </button>
              </div>
            )}

            {/* Other Players (revealed by Add friends) */}
            {playerCount > 1 && addFriends && (
              <div style={{ marginBottom: 24 }}>
                <p style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Other Players
                </p>
                {otherPlayers.map((p, i) => {
                  // Added player i occupies seat index i+1 (seat 0 = current
                  // user, who always uses the Primary Tees and gets no link).
                  const seatIndex = i + 1
                  const seatTee = playerTees[seatIndex]
                  // Expanded when the user tapped the link OR this seat already
                  // carries an alternate value (so a chosen tee is always
                  // visible/changeable). Collapse state is local UI only.
                  const teeExpanded = expandedTeeSeats.has(seatIndex) || !!seatTee
                  return (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <PlayerSearchInput
                        index={i}
                        value={p}
                        playerId={player?.id}
                        onChange={val => {
                          setOtherPlayers(prev => {
                            const arr = [...prev]
                            arr[i] = val
                            return arr
                          })
                        }}
                        footer={selectedCourse?.tees?.length > 0 ? (
                          <div style={{ marginTop: 8 }}>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => setExpandedTeeSeats(prev => {
                                const next = new Set(prev)
                                if (next.has(seatIndex)) next.delete(seatIndex)
                                else next.add(seatIndex)
                                return next
                              })}
                              style={{ fontSize: '0.78rem', color: 'var(--accent, #6ba368)', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              Alternate Tee Selection
                            </span>
                            {teeExpanded && (
                              <select
                                className="input"
                                value={seatTee || selectedTee || ''}
                                onChange={e => setPlayerTees(prev => ({ ...prev, [seatIndex]: e.target.value }))}
                                style={{ marginTop: 4 }}
                              >
                                {selectedCourse.tees.map(t => {
                                  const g = teeGenderSuffix(t)
                                  const name = g ? `${t.tee_name} (${g})` : t.tee_name
                                  const parts = [name]
                                  if (t.total_yardage) parts.push(`${t.total_yardage} yds`)
                                  const key = teeKey(t)
                                  return <option key={key} value={key}>{parts.join(' · ')}</option>
                                })}
                              </select>
                            )}
                          </div>
                        ) : null}
                      />
                    </div>
                  )
                })}
              </div>
            )}

            {/* Side games staging — sits above Start Round so the user
                sees the games attached to their round before committing.
                Hidden for solo rounds (min 2 players for every format).
                Panel self-collapses when empty; a summary line inside it
                shows how many staged. On commitRound, each entry is
                turned into a create_game call after init_standalone_round
                succeeds. See PreRoundGamesPanel + commitRound above. */}
            {playerCount >= 2 && (
              <PreRoundGamesPanel
                stagedGames={stagedGames}
                onChange={(next) => {
                  setStagedGames(next)
                  // A hand-driven change (add / remove / edit) supersedes the
                  // auto-unstage notice — hide it so it doesn't linger past a
                  // subsequent staged-list change the user drove themselves.
                  setStagingNotice(null)
                }}
                playerCount={playerCount}
                holeCount={holes === '9' ? 9 : 18}
                seatLabels={[
                  (player?.first_name || player?.name || 'You'),
                  ...otherPlayers.map((op, i) => (op?.name?.trim() || `Player ${i + 2}`)),
                ]}
                // F-100-T1: seat-indexed birthdates for the panel's
                // pre-flight KNOWN-under-18 refusal.
                //   * Seat 0 = the logged-in user's own birthdate.
                //   * Seats 1..N-1 = each linked friend's birthdate
                //     (F-100-T1 review fix: search_players now returns
                //     birthdate, PlayerSearchInput.pickPlayer forwards it
                //     onto the roster row as linked_birthdate, and this
                //     line reads it back so the pre-flight refusal fires
                //     for a friend added via the picker — the case that
                //     motivated the fix, previously silent-blocked only
                //     server-side at create_game time).
                //   * Stubs typed by name/phone (no linked_player_id) still
                //     have no birthdate here — the 18+ ack checkbox
                //     (acceptance 2) and the server-side gate cover them.
                seatBirthdates={[
                  player?.birthdate || null,
                  ...otherPlayers.map((op) => op?.linked_birthdate || null),
                ]}
              />
            )}

            {stagingNotice && (
              <div
                role="status"
                style={{
                  marginBottom: 12, padding: '10px 12px', borderRadius: 'var(--radius)',
                  background: 'rgba(212, 185, 122, 0.14)',
                  border: '1px solid var(--gold)',
                  color: 'var(--cream)', fontSize: '0.8rem',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}
              >
                <span style={{ flex: 1, lineHeight: 1.35 }}>{stagingNotice}</span>
                <button
                  onClick={() => setStagingNotice(null)}
                  aria-label="Dismiss"
                  style={{
                    background: 'none', border: 'none', color: 'var(--gray-300)',
                    fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, padding: '0 4px',
                    flexShrink: 0,
                  }}
                >×</button>
              </div>
            )}

            <button
              className="btn btn-primary btn-full"
              onClick={startRound}
              disabled={!canStart}
              style={{ padding: '16px 24px', fontSize: '1rem', fontWeight: 600 }}
            >
              Start Round
            </button>
          </>
        )}
      </div>

      {/* Team Setup popup — opens for every non-stroke format. Captures team
          assignments and per-player handicap toggles before the round starts.
          Scramble defaults to 1 team but can add more (e.g. 2v2 scramble);
          Match Play and Best Ball are fixed at 2 teams. */}
      {teamSetupOpen && (
        <div
          onClick={() => setTeamSetupOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 16px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--green-dark)',
              border: '1px solid var(--green-mid)',
              borderRadius: 12,
              padding: '20px 18px',
              maxWidth: 360, width: '100%',
              maxHeight: '85vh', overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <p style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 4 }}>
              Set up teams
            </p>
            <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', marginBottom: 14 }}>
              Check the team for each player.
              {selectedType === 'scramble' && ' Add more teams for multi-team scramble.'}
            </p>

            {/* Grid: [Name | Team 1 | Team 2 | ...]. Team cells are radio-style
                (one team per player). HCP column is currently hidden pending
                verification of whether handicap should apply to team formats —
                the underlying useHcp state + persistence still work so the
                column can come back without any logic changes. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `1fr repeat(${numTeams}, 56px)`,
                rowGap: 6, columnGap: 6,
                alignItems: 'center',
                marginBottom: 14,
              }}
            >
              {/* Header row */}
              <div />
              {Array.from({ length: numTeams }).map((_, t) => (
                <div key={`h-${t}`} style={teamHeader}>Team {t + 1}</div>
              ))}

              {/* Player rows */}
              {Array.from({ length: playerCount }).map((_, i) => {
                const isMe = i === 0
                const op = isMe ? null : otherPlayers[i - 1]
                const name = isMe
                  ? (player?.first_name || player?.name || 'You')
                  : (op?.name || `Player ${i + 1}`)
                const team = teamAssign[i] ?? 0
                return (
                  <Fragment key={i}>
                    <div style={teamRowName}>{name}</div>
                    {Array.from({ length: numTeams }).map((_, t) => (
                      <label key={`c-${i}-${t}`} style={teamCheckCell}>
                        <input
                          type="radio"
                          name={`team-${i}`}
                          checked={team === t}
                          onChange={() => setTeam(i, t)}
                          style={{ width: 18, height: 18, accentColor: 'var(--gold)', cursor: 'pointer' }}
                        />
                      </label>
                    ))}
                  </Fragment>
                )
              })}
            </div>

            {selectedType === 'scramble' && numTeams < playerCount - 1 && (
              <button
                type="button"
                onClick={addTeam}
                className="btn btn-ghost btn-full"
                style={{ marginBottom: 12, fontSize: '0.85rem', padding: '10px 14px' }}
              >
                + Add Team {numTeams + 1}
              </button>
            )}

            {!teamsValid() && (
              <p style={{ fontSize: '0.78rem', color: 'var(--red)', marginBottom: 10 }}>
                Every team needs at least one player.
              </p>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setTeamSetupOpen(false)}
                className="btn btn-ghost"
                style={{ flex: 1, padding: '12px 16px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmTeamSetup}
                disabled={!teamsValid()}
                className="btn btn-primary"
                style={{ flex: 1, padding: '12px 16px' }}
              >
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      <ParPickerModal
        open={parPickerOpen}
        onClose={() => setParPickerOpen(false)}
        holesCount={holes === '9' ? 9 : 18}
        initialPars={manualPars}
        onSave={async (pars) => {
          if (!selectedCourse?.id) return
          setParsSaving(true)
          try {
            // Persist server-side so the round, scorecard, and any later
            // session reading this course all see the user's pars.
            const res = await db('update_course_pars', { course_id: selectedCourse.id, pars })
            if (res?.error) throw new Error(res.error)
            setManualPars(pars)
            // Mirror onto the in-memory course so the UI reflects new par
            // immediately without a refetch.
            setSelectedCourse(prev => prev ? {
              ...prev,
              par: pars.reduce((s, p) => s + (p || 0), 0),
              holes_data: pars.map((p, i) => ({ hole_number: i + 1, par: p })),
            } : prev)
            setParPickerOpen(false)
          } catch (err) {
            console.error('Save pars failed:', err)
            // Modal stays open so the user can retry without losing input.
          }
          setParsSaving(false)
        }}
      />
    </div>
  )
}
